import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, PauseCircle, X, Wallet, Mail, Send, Check, ShieldAlert,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * AccountsPOActionModal — Phase 5, `accounts_po_review` (Sangita.K).
 *
 * Rules (per Phase 4 / Phase 5 final spec):
 *   • Approve  → `payment_source` MANDATORY (school|student_imprest|parents).
 *                Conditional branches:
 *                  - student_imprest → `imprest_balance_available` (yes|no) +
 *                    `imprest_remark` (>= 10 chars). The imprest remark is a
 *                    business rule and stays mandatory regardless of action.
 *                  - parents → `parent_email` MANDATORY. The "Send Payment
 *                    Details" button must be clicked BEFORE Approve is
 *                    enabled. Backend also enforces this.
 *                General remarks OPTIONAL on Approve.
 *   • Hold/Reject → remarks MANDATORY (min 2 chars); payment_source ignored.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function AccountsPOActionModal({ open, onClose, onDone, requisition, action, currentUser, actingAsBanner = null }) {
  const [remarks, setRemarks] = useState("");
  const [paymentSource, setPaymentSource] = useState("");
  const [imprestBalance, setImprestBalance] = useState(""); // "yes" | "no"
  const [imprestRemark, setImprestRemark] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  // Track whether the payment-details email was sent AFTER opening this modal
  const [emailSentAt, setEmailSentAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setPaymentSource(""); setImprestBalance("");
    setImprestRemark(""); setError(""); setSendingEmail(false);
    // Prefill parent email + sent timestamp from the requisition if the
    // Sangita has already sent it earlier (reopening the modal shouldn't
    // force her to click Send again).
    const fd = requisition?.form_data || {};
    setParentEmail(fd.parent_email || "");
    setEmailSentAt(fd.parent_payment_email_sent_at || null);
    setTimeout(() => textareaRef.current?.focus(), 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const cfg = {
    approve: { label: "Approve", pastTense: "Approved", Icon: CheckCircle2, tone: "teal",
               hint: "Select the payment source and complete the branch-specific fields." },
    on_hold: { label: "Place on Hold", pastTense: "On-hold", Icon: PauseCircle, tone: "gold",
               hint: "The applicant will be asked to revise the requisition." },
    reject:  { label: "Reject", pastTense: "Rejected", Icon: XCircle, tone: "danger",
               hint: "The requisition will be closed with your reason." },
  }[action];
  if (!cfg) return null;

  // Phase 15.1 — At the fnb v15 `accounts_imprest_check` stage, this modal
  // renders in a SIMPLIFIED imprest-only mode: Yes/No radio + optional
  // Remarks; no Payment Source dropdown, no parent-email branch.
  const isImprestOnlyStage = requisition?.current_stage === "accounts_imprest_check";

  const trimmedRemarks = remarks.trim();
  const trimmedImprestRemark = imprestRemark.trim();
  const trimmedParentEmail = parentEmail.trim().toLowerCase();

  const remarksMandatory = action !== "approve";
  const remarksOk = true; // Phase 12.1 — no length rule

  // Approve branch validation
  let approveOk = true;
  let approveHint = "";
  if (action === "approve") {
    if (isImprestOnlyStage) {
      // Yes/No optional in the UI — backend defaults to 'yes' if omitted.
      // If 'no' is selected, remarks must be at least 2 chars (Phase 12).
      if (imprestBalance === "no" && trimmedRemarks.length < 2) {
        approveOk = false;
        approveHint = "Remarks are mandatory (min 2 chars) when imprest is not available.";
      }
    } else if (!paymentSource) {
      approveOk = false;
      approveHint = "Select a payment source.";
    } else if (paymentSource === "student_imprest") {
      if (!imprestBalance) {
        approveOk = false;
        approveHint = "Choose whether imprest balance is available.";
      } else if (trimmedImprestRemark.length < 10) {
        approveOk = false;
        approveHint = "Imprest remark is mandatory (min 10 characters).";
      }
    } else if (paymentSource === "parents") {
      if (!EMAIL_RE.test(trimmedParentEmail)) {
        approveOk = false;
        approveHint = "Enter a valid parent email.";
      } else if (!emailSentAt) {
        approveOk = false;
        approveHint = "Click 'Send Payment Details' to email the parent before approving.";
      }
    }
  }

  const canSubmit = remarksOk && approveOk && !busy;

  const btnCls = {
    teal:   "bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a]",
    gold:   "bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f]",
    danger: "bg-[#b3261e] hover:bg-[#8a1c17] border-[#8a1c17]",
  }[cfg.tone];

  const sendPaymentEmail = async () => {
    if (!EMAIL_RE.test(trimmedParentEmail)) {
      toast.error("Enter a valid parent email first.");
      return;
    }
    setSendingEmail(true);
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/send-parent-payment`,
        { parent_email: trimmedParentEmail },
      );
      setEmailSentAt(data.at || new Date().toISOString());
      toast.success(`Payment details sent to ${trimmedParentEmail}.`);
    } catch (err) {
      toast.error(formatApiError(err), { duration: 5000 });
    } finally {
      setSendingEmail(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const payload = { action, remarks: trimmedRemarks };
      if (action === "approve") {
        if (isImprestOnlyStage) {
          if (imprestBalance) {
            payload.imprest_balance_available = imprestBalance;
          }
        } else {
          payload.payment_source = paymentSource;
          if (paymentSource === "student_imprest") {
            payload.imprest_balance_available = imprestBalance;
            payload.imprest_remark = trimmedImprestRemark;
          }
          if (paymentSource === "parents") {
            payload.parent_email = trimmedParentEmail;
          }
        }
      }
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        payload,
      );
      toast.success(`${cfg.pastTense} — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="accounts-po-modal"
    >
      <div
        className="w-full max-w-xl bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Wallet className="text-[#7a1f2b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Accounts PO · {cfg.label} — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>
                You are acting as <strong>{actingAsBanner}</strong> on behalf
                of the assigned approver. Logged as a super-admin bypass.
              </div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#b48a45] px-3 py-2 rounded">
            <strong>{cfg.hint}</strong>
          </div>

          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}

          {action === "approve" && isImprestOnlyStage && (
            <div className="rounded border border-[#e2d7c3] bg-white p-3 space-y-3"
                  data-testid="imprest-only-branch">
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Imprest Balance Available?
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">
                    optional — defaults to Yes
                  </span>
                </span>
                <div className="flex items-center gap-4 text-sm">
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" name="imprest-only" value="yes"
                            checked={imprestBalance === "yes"}
                            onChange={(e) => setImprestBalance(e.target.value)}
                            data-testid="apo-imprest-yes" />
                    Yes
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" name="imprest-only" value="no"
                            checked={imprestBalance === "no"}
                            onChange={(e) => setImprestBalance(e.target.value)}
                            data-testid="apo-imprest-no" />
                    No
                  </label>
                </div>
              </label>
            </div>
          )}

          {action === "approve" && !isImprestOnlyStage && (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Payment Source <span className="text-[#b3261e]">*</span>
                </span>
                <select
                  value={paymentSource}
                  onChange={(e) => setPaymentSource(e.target.value)}
                  className="input-vgws"
                  data-testid="payment-source-select"
                >
                  <option value="">Select payment source…</option>
                  <option value="school">School (pays directly)</option>
                  <option value="student_imprest">Student Imprest</option>
                  <option value="parents">Parents (invoice / bank details)</option>
                </select>
              </label>

              {paymentSource === "student_imprest" && (
                <div className="rounded border border-[#e2d7c3] bg-white p-3 space-y-3"
                     data-testid="imprest-branch">
                  <label className="block">
                    <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                      Imprest Balance Available? <span className="text-[#b3261e]">*</span>
                    </span>
                    <div className="flex items-center gap-4 text-sm">
                      <label className="inline-flex items-center gap-1.5">
                        <input type="radio" name="imprest-bal" value="yes"
                               checked={imprestBalance === "yes"}
                               onChange={(e) => setImprestBalance(e.target.value)}
                               data-testid="imprest-yes" />
                        Yes
                      </label>
                      <label className="inline-flex items-center gap-1.5">
                        <input type="radio" name="imprest-bal" value="no"
                               checked={imprestBalance === "no"}
                               onChange={(e) => setImprestBalance(e.target.value)}
                               data-testid="imprest-no" />
                        No
                      </label>
                    </div>
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                      Imprest Remark <span className="text-[#b3261e]">*</span>
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 10 chars</span>
                    </span>
                    <textarea
                      value={imprestRemark}
                      onChange={(e) => setImprestRemark(e.target.value.slice(0, 500))}
                      rows={2}
                      placeholder="e.g. Deducted from Student X imprest, balance ₹8,500"
                      className={`input-vgws ${
                        trimmedImprestRemark && trimmedImprestRemark.length < 10
                          ? "!border-[#b3261e]" : ""
                      }`}
                      data-testid="imprest-remark"
                    />
                  </label>
                </div>
              )}

              {paymentSource === "parents" && (
                <div className="rounded border border-[#e2d7c3] bg-white p-3 space-y-3"
                     data-testid="parents-branch">
                  <label className="block">
                    <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                      Parent Email <span className="text-[#b3261e]">*</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
                        <input
                          type="email"
                          value={parentEmail}
                          onChange={(e) => {
                            setParentEmail(e.target.value);
                            // If the user changed the address after sending,
                            // require a fresh send.
                            if (emailSentAt) setEmailSentAt(null);
                          }}
                          placeholder="parent@example.com"
                          className={`input-vgws pl-9 ${
                            parentEmail && !EMAIL_RE.test(trimmedParentEmail)
                              ? "!border-[#b3261e]" : ""
                          }`}
                          data-testid="parent-email-input"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={sendPaymentEmail}
                        disabled={sendingEmail || !EMAIL_RE.test(trimmedParentEmail)}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white border shadow-sm bg-[#7a1f2b] hover:bg-[#5a1520] border-[#5a1520] ${(sendingEmail || !EMAIL_RE.test(trimmedParentEmail)) ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid="send-parent-payment-btn"
                      >
                        {sendingEmail
                          ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <Send size={12} />}
                        {sendingEmail ? "Sending…" : "Send Payment Details"}
                      </button>
                    </div>
                    {emailSentAt && (
                      <div className="mt-1 text-xs text-[#2d6b6b] inline-flex items-center gap-1"
                           data-testid="parent-email-sent-marker">
                        <Check size={12} /> Sent to {trimmedParentEmail} at {new Date(emailSentAt).toLocaleString()}
                      </div>
                    )}
                    {!emailSentAt && EMAIL_RE.test(trimmedParentEmail) && (
                      <div className="mt-1 text-xs text-[#b48a45]">
                        Click "Send Payment Details" before approving.
                      </div>
                    )}
                  </label>
                </div>
              )}
            </>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks {remarksMandatory
                ? <span className="text-[#b3261e]">*</span>
                : <span className="text-[#6e7770] text-xs font-normal">(optional)</span>}
              {remarksMandatory && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 2 chars</span>
              )}
            </span>
            <textarea
              ref={textareaRef}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
              rows={3}
              placeholder={remarksMandatory
                ? "Describe your reason clearly…"
                : "Add any note for the record (optional)…"}
              className={`input-vgws min-h-[80px] ${
                remarksMandatory && trimmedRemarks && !remarksOk ? "!border-[#b3261e]" : ""
              }`}
              data-testid={isImprestOnlyStage ? "apo-remark" : "accounts-po-remarks"}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={remarksMandatory && !remarksOk ? "text-[#b3261e]" : "text-[#2d6b6b]"}>
                {remarksMandatory
                  ? (remarksOk ? "Looks good." : "Mandatory for hold/reject (min 2 characters).")
                  : "Optional for approve."}
              </span>
              <span className="font-mono text-[#6e7770]">{trimmedRemarks.length} / 1000</span>
            </div>
          </label>

          {action === "approve" && !approveOk && (
            <div className="text-xs text-[#b48a45] bg-[#faf5eb] px-3 py-2 rounded border border-[#b48a45]/30"
                 data-testid="approve-hint">
              {approveHint}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="accounts-po-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm ${btnCls} ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid={isImprestOnlyStage ? "apo-submit" : "accounts-po-confirm"}
          >
            {busy
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <cfg.Icon size={16} />}
            {busy ? "Please wait…" : `Confirm ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
